"use client";

import { useState } from "react";
import { LeadForm, type FieldConfig } from "@/components/forms/lead-form";
import { Button } from "@/components/ui/button";
import { practiceOutcomes, practicePlatforms } from "@/lib/practice-lead";

type SingleStep = {
  id: "outcome" | "spend";
  label: string;
  prompt: string;
  options: string[];
};

type PlatformStep = {
  id: "platforms";
  label: string;
  prompt: string;
  options: string[];
  multiple: true;
};

type DetailsStep = {
  id: "details";
  label: string;
  prompt: string;
};

const steps: (SingleStep | PlatformStep | DetailsStep)[] = [
  {
    id: "outcome",
    label: "Business outcome",
    prompt: "What growth outcome matters most right now?",
    options: ["Reduce wasted spend", "Generate more qualified pipeline", "Improve conversion quality"],
  },
  {
    id: "spend",
    label: "Platform spend",
    prompt: "What is your estimated platform spend?",
    options: ["Less than $5,000", "$5,000 to $10,000", "$10,000 to $30,000", "$30,000+"],
  },
  {
    id: "platforms",
    label: "Current platforms",
    prompt: "What platforms are you currently using?",
    options: ["Google Ads", "Bing Ads", "Meta", "ChatGPT", "YouTube", "LinkedIn"],
    multiple: true,
  },
  {
    id: "details",
    label: "Your details",
    prompt: "Where should we send your growth analysis?",
  },
];

const detailFields: FieldConfig[] = [
  { name: "name", label: "Full name", type: "text", required: true, autoComplete: "name" },
  { name: "email", label: "Work email", type: "email", required: true, autoComplete: "email" },
  { name: "company", label: "Company", type: "text", required: true, autoComplete: "organization" },
  { name: "website", label: "Website", type: "text", required: true, placeholder: "example.com" },
];

type Answers = {
  outcome: string;
  spend: string;
  platforms: string[];
};

type WizardProps = {
  source?: string;
  successRedirect?: string;
  title?: string;
  practice?: "healthcare" | "dental";
  landingPage?: string;
  submitLabel?: string;
};

export function QualificationWizard({
  source = "growth-audit",
  successRedirect = "/thank-you",
  title = "Find your biggest growth opportunity.",
  practice,
  landingPage,
  submitLabel,
}: WizardProps = {}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ outcome: "", spend: "", platforms: [] });
  const [details, setDetails] = useState<Record<string, string>>({});
  const practiceSteps = practice ? steps.map((item) => item.id === "outcome" ? {
    ...item, prompt: "What would help your practice most right now?",
    options: practiceOutcomes,
  } : item.id === "platforms" ? { ...item, options: practicePlatforms } : item.id === "details" ? { ...item, prompt: "Where can we reach you about your practice?" } : item) : steps;
  const current = practiceSteps[step];
  const selected = current.id === "platforms" ? answers.platforms : current.id === "details" ? "" : answers[current.id];
  const canContinue = Array.isArray(selected) ? selected.length > 0 : Boolean(selected);

  const choose = (option: string) => {
    if (current.id === "platforms") {
      setAnswers((previous) => ({
        ...previous,
        platforms: practice && option === "Not running campaigns yet" ? (previous.platforms.includes(option) ? [] : [option]) : previous.platforms.includes(option)
          ? previous.platforms.filter((item) => item !== option)
          : [...previous.platforms.filter((item) => !practice || item !== "Not running campaigns yet"), option],
      }));
      return;
    }
    setAnswers((previous) => ({ ...previous, [current.id]: option }));
  };

  return (
    <section className="qualification-wizard" aria-labelledby="qualification-title">
      <div className="qualification-wizard-head">
        <div>
          <p className="qualification-kicker">A quick way to start</p>
          <h2 id="qualification-title">{title}</h2>
        </div>
        <span className="qualification-count" aria-label={`Step ${step + 1} of ${steps.length}`}>
          0{step + 1} / 0{steps.length}
        </span>
      </div>

      <div className="qualification-progress" aria-hidden="true">
        <span style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>

      <p className="qualification-step-label">{current.label}</p>
      <p className="qualification-prompt" id="qualification-prompt">{current.prompt}</p>
      {current.id === "platforms" ? <p className="qualification-selection-hint">Select all that apply.</p> : null}

      {current.id === "details" ? (
        <div className="qualification-details">
          <LeadForm
            fields={practice ? detailFields.map((field) => field.name === "company" ? { ...field, label: "Practice name" } : field) : detailFields}
            submitLabel={submitLabel ?? (practice ? "Request a Review" : "Talk to an Expert")}
            submitWithArrow
            source={source}
            successRedirect={successRedirect}
            onValuesChange={setDetails}
            intro={practice ? "For practice owners and marketing teams—not patient appointment requests. Please don’t include patient or health information." : "A senior strategist will review your answers and follow up with useful context—not a sales queue."}
            initialValues={{
              ...details,
              ...(practice ? { practice_type: practice } : {}),
              ...(landingPage ? { form_page: landingPage } : {}),
              qualification_outcome: answers.outcome,
              qualification_spend: answers.spend,
              qualification_platforms: answers.platforms.join(", "),
            }}
          />
        </div>
      ) : (
        <>
          <div
            className={`qualification-options qualification-options--${current.id}`}
            role={current.id === "platforms" ? "group" : "radiogroup"}
            aria-labelledby="qualification-prompt"
          >
            {current.options.map((option) => {
              const isSelected = Array.isArray(selected) ? selected.includes(option) : selected === option;
              return (
                <button
                  type="button"
                  className={`qualification-option${isSelected ? " is-selected" : ""}`}
                  key={option}
                  aria-checked={isSelected}
                  role={current.id === "platforms" ? "checkbox" : "radio"}
                  tabIndex={current.id === "platforms" || isSelected || (!selected && current.options[0] === option) ? 0 : -1}
                  onKeyDown={(event) => {
                    if (current.id === "platforms") return;
                    const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : ["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 0;
                    if (!direction && !["Home", "End"].includes(event.key)) return;
                    event.preventDefault();
                    const index = event.key === "Home" ? 0 : event.key === "End" ? current.options.length - 1 : (current.options.indexOf(option) + direction + current.options.length) % current.options.length;
                    choose(current.options[index]);
                    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("button")[index]?.focus();
                  }}
                  onClick={() => choose(option)}
                >
                  <span
                    className={`qualification-option-marker${current.id === "platforms" ? " qualification-option-marker--checkbox" : ""}`}
                    aria-hidden="true"
                  />
                  {option}
                </button>
              );
            })}
          </div>

          <div className="qualification-actions">
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep((value) => value - 1)}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Button variant="accent" withArrow disabled={!canContinue} onClick={() => setStep((value) => value + 1)}>
              Next
            </Button>
          </div>

          <p className="qualification-note">Three questions, then your details. No commitment.</p>
        </>
      )}

      {current.id === "details" ? (
        <div className="qualification-details-back">
          <Button variant="ghost" onClick={() => setStep((value) => value - 1)}>
            Back to questions
          </Button>
        </div>
      ) : null}
    </section>
  );
}
